import React, { CSSProperties, memo, MutableRefObject, useCallback, useMemo } from 'react'
import { SlSkeleton } from '@shoelace-style/shoelace/dist/react'
import { Currency, Token } from '@nominex/nomiswap-sdk'
import { Text } from '@nominex/dex-uikit'
import tw from 'twin.macro'
import styled from 'styled-components'
import { FixedSizeList } from 'react-window'
import { isTokenOnList } from 'utils'
import QuestionHelper from 'components/QuestionHelper'
import { CurrencyImage } from 'components/CurrencyImage'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import useNativeCurrency from 'hooks/useNativeCurrency'
import { useCombinedActiveList } from 'state/lists/hooks'
import { useCurrencyBalance } from 'state/wallet/hooks'
import { useIsUserAddedToken } from 'hooks/Tokens'
import { Column } from '../Layout'

function currencyKey(currency: Currency): string {
  return currency?.isToken ? currency.address : currency?.isNative ? currency.symbol : ''
}

function getItemKey(index: number, data: any): string {
  return currencyKey(data[index])
}

const CurrencyRowItem = styled.div<{ disabled: boolean }>`
  ${tw`text-white grid gap-8 items-center px-20 py-4 hover:bg-dark-gray-900 h-[56px] cursor-pointer`}
  grid-template-columns: auto minmax(auto, 1fr) minmax(0, 80px);
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`

const CurrencyBalanceRow = memo(({ currency, account }: { currency: Currency; account: string }) => {
  const balance = useCurrencyBalance(account, currency)
  return (
    <div className="justify-self-end">
      {balance ? (
        <div className="text-16 font-medium text-ellipsis overflow-hidden max-w-[80px]" title={balance.toExact()}>
          {balance.toSignificant(4)}
        </div>
      ) : account ? (
        <SlSkeleton effect="sheen" className="w-[60px] h-[16px]" />
      ) : null}
    </div>
  )
})

const CurrencyRow = memo(
  ({
    currency,
    isSelected,
    style,
    onSelect,
    account,
  }: {
    currency: Currency
    isSelected: boolean
    style: CSSProperties
    account: string
    onSelect: () => void
  }) => {
    const selectedTokenList = useCombinedActiveList()
    const isOnSelectedList = isTokenOnList(selectedTokenList, currency)
    const customAdded = useIsUserAddedToken(currency)

    // only show add or remove buttons if not on selected list
    return (
      <CurrencyRowItem style={style} onClick={() => (isSelected ? null : onSelect())} disabled={isSelected}>
        <CurrencyImage currency={currency} size="32px" />
        <Column>
          <Text bold noTranslate>
            {currency.symbol}
          </Text>
          <Text color="darkGray100" small ellipsis maxWidth="200px" noTranslate>
            {!isOnSelectedList && customAdded && 'Added by user •'} {currency.name}
          </Text>
        </Column>
        <CurrencyBalanceRow currency={currency} account={account} />
      </CurrencyRowItem>
    )
  },
)

const ListRow = memo(({ data, index, selectedCurrency, onCurrencySelect, style }: any) => {
  const { t } = useTranslation()
  const { account } = useWagmi()
  const currency: Currency = data[index]
  const isSelected = Boolean(selectedCurrency && currency && selectedCurrency.equals(currency))
  const handleSelect = () => onCurrencySelect(currency)

  return currency ? (
    <CurrencyRow style={style} account={account} currency={currency} isSelected={isSelected} onSelect={handleSelect} />
  ) : (
    <div className="text-white text-14 font-medium flex items-center px-20" style={style}>
      <span>{t('Expanded search results')}</span>
      <QuestionHelper
        content={t(
          "Tokens from inactive lists. Import specific tokens below or click 'Manage tokens' to activate more lists.",
        )}
        placement="right-start"
      />
    </div>
  )
})

type CurrencyListProps = {
  height: number
  currencies: Currency[]
  inactiveCurrencies: Currency[]
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  fixedListRef?: MutableRefObject<FixedSizeList | undefined>
  showImportView: () => void
  setImportToken: (token: Token) => void
}

function CurrencyList({
  height,
  currencies,
  inactiveCurrencies,
  selectedCurrency,
  onCurrencySelect,
  fixedListRef,
  showImportView,
  setImportToken,
}: CurrencyListProps) {
  const native = useNativeCurrency()
  const itemData: (Currency | undefined)[] = useMemo(() => {
    const nmxFirstList = [...currencies].sort(({ symbol }) => (symbol === 'NMX' ? -1 : 1))

    return inactiveCurrencies.length
      ? [native, ...nmxFirstList, null, ...inactiveCurrencies]
      : [native, ...nmxFirstList]
  }, [currencies, inactiveCurrencies, native])

  const Row = useCallback(
    (properties) => (
      <ListRow
        selectedCurrency={selectedCurrency}
        onCurrencySelect={onCurrencySelect}
        currencies={currencies}
        showImportView={showImportView}
        setImportToken={setImportToken}
        {...properties}
      />
    ),
    [currencies, onCurrencySelect, selectedCurrency, setImportToken, showImportView],
  )

  return (
    <FixedSizeList
      height={height}
      ref={fixedListRef as any}
      width="100%"
      itemData={itemData}
      itemCount={itemData.length}
      itemSize={56}
      itemKey={getItemKey}
    >
      {Row}
    </FixedSizeList>
  )
}

export default memo(CurrencyList)
